/**
 * Le QR de paiement européen — norme EPC069-12, « SEPA Credit Transfer ».
 *
 * ## Ce que c'est, et ce que ce n'est pas
 *
 * Ce n'est **pas** un lien de paiement : rien n'est débité en le scannant. Le
 * QR ne fait que **pré-remplir un virement** dans l'application bancaire du
 * parent — bénéficiaire, IBAN, montant, communication. Il valide ensuite
 * lui-même, avec ses propres moyens d'authentification. Nous ne voyons jamais
 * ses identifiants, et il n'y a aucun frais.
 *
 * C'est la norme que lisent les applications belges (Belfius, KBC, ING, BNP
 * Paribas Fortis, Argenta…). Elle est publiée par le European Payments
 * Council, et le format est figé : **onze lignes, dans cet ordre**.
 *
 * ## Le format, ligne par ligne
 *
 *      1  BCD                       en-tête, invariable
 *      2  002                       version
 *      3  1                         1 = UTF-8
 *      4  SCT                       SEPA Credit Transfer
 *      5  BBRUBEBB                  BIC du bénéficiaire
 *      6  Éclosion ASBL             nom du bénéficiaire, 70 caractères
 *      7  BE64363110057452          IBAN, sans espaces
 *      8  EUR105.00                 devise et montant
 *      9  CHAR                      code raison, facultatif
 *     10  <référence structurée>     OU
 *     11  <communication libre>      — jamais les deux
 *
 * **Les lignes 10 et 11 s'excluent.** La ligne 10 attend une référence
 * ISO 11649 (`RF…`), que la Belgique n'utilise pas : notre communication
 * structurée belge va donc en ligne 11, sous sa forme `+++100/2602/02433+++`,
 * que les applications belges reconnaissent et recopient dans le champ dédié.
 *
 * ## Les limites qui comptent
 *
 * Le montant doit tenir entre 0,01 et 999 999 999,99 €, et la charge utile ne
 * doit pas dépasser **331 octets** : au-delà, la norme n'est plus respectée et
 * certaines applications refusent le code.
 */

/** Ce qu'un virement a besoin de savoir. */
export interface Virement {
  /** Le nom du bénéficiaire, tel qu'il apparaîtra dans l'app bancaire. */
  beneficiaire: string;
  iban: string;
  bic?: string;
  montantCentimes: number;
  /** La communication, structurée `+++…+++` ou libre. */
  communication: string;
}

export const ECLOSION: Omit<Virement, 'montantCentimes' | 'communication'> = {
  beneficiaire: 'Eclosion ASBL',
  iban: 'BE64363110057452',
  bic: 'BBRUBEBB',
};

/** La limite de la norme. Au-delà, le code n'est plus un EPC valide. */
export const TAILLE_MAX_OCTETS = 331;

export class VirementInvalide extends Error {}

/** L'IBAN sans espaces ni ponctuation, en majuscules. */
export function normaliserIban(iban: string): string {
  return (iban ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

/*
  Le nom du bénéficiaire est plafonné à 70 caractères par la norme. On coupe
  plutôt que d'émettre un code hors norme : une application qui le refuse
  laisserait le parent sans moyen de payer.
*/
function nom(v: string): string {
  return (v ?? '').trim().slice(0, 70);
}

/**
 * La charge utile du QR.
 *
 * Rend la chaîne exacte à encoder. Lève si le virement ne peut pas être
 * représenté — mieux vaut ne pas afficher de QR qu'en afficher un que la
 * banque refuse.
 */
export function chargeUtileEpc(v: Virement): string {
  const iban = normaliserIban(v.iban);
  if (!/^BE[0-9]{14}$/.test(iban) && !/^[A-Z]{2}[0-9]{2}[A-Z0-9]{10,30}$/.test(iban)) {
    throw new VirementInvalide(`IBAN « ${v.iban} » invalide.`);
  }
  if (!Number.isInteger(v.montantCentimes) || v.montantCentimes < 1) {
    throw new VirementInvalide('Un virement porte un montant strictement positif.');
  }
  if (v.montantCentimes > 99_999_999_999) {
    throw new VirementInvalide('Le montant dépasse ce que la norme autorise.');
  }
  if (!nom(v.beneficiaire)) {
    throw new VirementInvalide('Le bénéficiaire est obligatoire.');
  }

  const montant = `EUR${(v.montantCentimes / 100).toFixed(2)}`;

  /*
    La communication belge `+++…+++` n'est PAS une référence ISO 11649 : elle
    va en communication libre (ligne 11), et la ligne 10 reste vide. Une
    application belge y reconnaît la forme et la replace dans son champ dédié.

    La ligne libre est plafonnée à 140 caractères par la norme.
  */
  const libre = (v.communication ?? '').trim().slice(0, 140);

  const assembler = (beneficiaire: string) => [
    'BCD',
    '002',
    '1',
    'SCT',
    v.bic ? v.bic.replace(/\s+/g, '').toUpperCase() : '',
    beneficiaire,
    iban,
    montant,
    '',        // code raison : rien à déclarer
    '',        // référence structurée ISO 11649 : inutilisée en Belgique
    libre,
  ].join('\n');

  /*
    La norme pose DEUX limites, et elles ne se recouvrent pas : 70 caractères
    pour le nom, 140 pour la communication libre, mais aussi **331 octets** au
    total. Un nom accentué compte deux octets par lettre : soixante-dix `É`
    font cent quarante octets, et le total déborde alors que chaque champ tient
    dans sa limite.

    Ce qu'on raccourcit alors : le NOM. Il est décoratif — c'est l'IBAN qui
    dirige l'argent, et la banque affiche de toute façon le nom du titulaire
    réel du compte. La communication, elle, ne se raccourcit pas : une
    communication structurée tronquée est invalide, et le paiement arriverait
    sans que le rapprochement sache à quelle facture.
  */
  let beneficiaire = nom(v.beneficiaire);
  let charge = assembler(beneficiaire);
  while (new TextEncoder().encode(charge).length > TAILLE_MAX_OCTETS
         && beneficiaire.length > 1) {
    beneficiaire = beneficiaire.slice(0, -1).trimEnd();
    charge = assembler(beneficiaire);
  }

  const octets = new TextEncoder().encode(charge).length;
  if (octets > TAILLE_MAX_OCTETS) {
    /*
      Il ne reste plus rien à raccourcir sans mentir. Mieux vaut ne pas
      afficher de QR qu'en afficher un que la banque refusera sans expliquer
      pourquoi.
    */
    throw new VirementInvalide(
      `La charge utile fait ${octets} octets, la norme en autorise ${TAILLE_MAX_OCTETS}.`,
    );
  }
  return charge;
}

/**
 * Le virement d'une facture, prêt à encoder.
 *
 * `remainingCents` et non le montant de la facture : si une provision en a
 * déjà payé une partie, le QR doit demander le RESTE. Pré-remplir le montant
 * total ferait payer deux fois.
 */
export function virementDeFacture(
  communication: string, remainingCents: number,
): Virement {
  return { ...ECLOSION, montantCentimes: remainingCents, communication };
}
